/**
 * KicadPluginInstallCard — surfaces the latest plugin zip with a
 * one-click download + install instructions.
 *
 * Pulls release metadata from `/v1/plugin/kicad/latest` and links to
 * `/v1/plugin/kicad/download`. The download endpoint 302s to the
 * GitHub release asset so we keep zero-egress while still seeing the
 * tap in our backend logs.
 *
 * The card stays compact when collapsed (just version + button) and
 * expands a "How to install" panel for first-time users — most repeat
 * users won't need to read it twice.
 */
import {
  CheckCircleRounded,
  DesktopMacRounded,
  DownloadRounded,
  ExpandMoreRounded,
  HelpOutlineRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { pluginDistributionApi } from "../services/api";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface KicadPluginInstallCardProps {
  /** When true, renders a slimmer variant for embedding in other cards
   *  (e.g. the project Plugin Sync card on the overview page). The
   *  default `false` is the standalone Settings → API Keys page shape. */
  compact?: boolean;
}

export function KicadPluginInstallCard({
  compact = false,
}: KicadPluginInstallCardProps) {
  const release = useQuery({
    queryKey: ["kicad-plugin-latest"],
    queryFn: pluginDistributionApi.getLatestKicadRelease,
    // 5-min cache aligns with the backend's own GitHub-API cache so
    // we don't waste a fetch when the user navigates away and back.
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const downloadUrl = pluginDistributionApi.downloadKicadUrl();

  const Body = (
    <Stack spacing={2}>
      {release.isLoading && (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={20} />
          <Skeleton variant="text" width={180} height={28} />
        </Stack>
      )}

      {release.isError && (
        <Alert severity="warning" icon={<WarningAmberRounded />}>
          Couldn't reach the plugin release feed. You can still grab the zip
          directly from{" "}
          <a
            href="https://github.com/octoco-ltd/circuit-forge/releases?q=kicad-plugin"
            target="_blank"
            rel="noreferrer"
          >
            GitHub Releases
          </a>
          .
        </Alert>
      )}

      {release.data && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2">
              <strong>v{release.data.version}</strong>
            </Typography>
            {release.data.asset && (
              <Chip
                size="small"
                variant="outlined"
                label={formatBytes(release.data.asset.size)}
              />
            )}
            {release.data.published_at && (
              <Typography variant="caption" color="text.secondary">
                Released{" "}
                {new Date(release.data.published_at).toLocaleDateString()}
              </Typography>
            )}
          </Stack>

          <Button
            component="a"
            href={downloadUrl}
            variant="contained"
            startIcon={<DownloadRounded />}
            disabled={!release.data.asset}
          >
            Download plugin
          </Button>
        </Stack>
      )}

      <Accordion
        elevation={0}
        disableGutters
        sx={{ "&:before": { display: "none" } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRounded />}
          sx={{
            px: 0,
            minHeight: 36,
            "& .MuiAccordionSummary-content": { my: 0.5 },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <HelpOutlineRounded fontSize="small" color="action" />
            <Typography variant="body2">How to install</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <List dense>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Click Download plugin above to save the .zip."
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    In KiCad, open{" "}
                    <strong>
                      Tools → Plugin and Content Manager → Install from File…
                    </strong>{" "}
                    and pick the zip.
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Restart pcbnew. In <strong>Tools → External Plugins</strong>
                    , click <em>Connect to Circuit Forge</em>, accept the
                    pre-filled URL, and paste your API key + project ID.
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleRounded fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    Push and pull from{" "}
                    <strong>
                      Tools → External Plugins → Push to / Pull from Circuit
                      Forge
                    </strong>
                    .
                  </>
                }
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );

  // Branded header reused by both compact + standalone variants. The
  // colored avatar + "Desktop" form-factor chip give the card a
  // distinctive identity vs. the EasyEDA card sitting next to it.
  const Header = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar
        variant="rounded"
        sx={{
          width: 36,
          height: 36,
          bgcolor: "#314CB0",
          color: "common.white",
          fontWeight: 700,
        }}
      >
        K
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            KiCad plugin
          </Typography>
          <Chip
            size="small"
            icon={<DesktopMacRounded sx={{ fontSize: 14 }} />}
            label="Desktop"
            variant="outlined"
            sx={{ height: 20 }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          KiCad 7+ on Windows, macOS, Linux
        </Typography>
      </Box>
    </Stack>
  );

  if (compact) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
        <Stack spacing={2}>
          {Header}
          <Divider />
          {Body}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        {Header}
        <Typography variant="body2" color="text.secondary">
          Once installed, the plugin pushes your designs from KiCad to Circuit
          Forge with one click.
        </Typography>
        <Divider />
        {Body}
      </Stack>
    </Paper>
  );
}
